import { createSelector } from 'reselect';

import { DataKadoGraph, DataKadoGraphParams } from '../../domain/dataKadoGraph';
import { KADO_FIELD_TYPE, KadoFieldType } from '../../domain/schemas';
import { ShuOption } from '../../domain/shu';

import { SelectOption } from '../../components/molecules/SingleSelect';

import { RootState } from '../../store';
import { getSelectedHallsAndAreas } from '../../utils/getSelectedHallsAndAreas';
import { dataChainStoreShuDataByShuSelector } from './dataChainStoreShu';
import { dataChainStoreShuWholeDataSelector } from './dataChainStoreShuWhole';

// Action Types

const FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH =
  'FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH' as const;
const FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_REQUEST =
  'FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_REQUEST' as const;
const FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_SUCCESS =
  'FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_SUCCESS' as const;
const SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH =
  'SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH' as const;
const SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_HALL =
  'SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_HALL' as const;
const SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_KADO_TYPE =
  'SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_KADO_TYPE' as const;
const HIDE_DATA_CHAIN_STORE_SHU_KADO_GRAPH =
  'HIDE_DATA_CHAIN_STORE_SHU_KADO_GRAPH' as const;
const HIDE_ALL_DATA_CHAIN_STORE_SHU_KADO_GRAPH =
  'HIDE_ALL_DATA_CHAIN_STORE_SHU_KADO_GRAPH' as const;
const RENEW_DATA_CHAIN_STORE_SHU_KADO_GRAPH =
  'RENEW_DATA_CHAIN_STORE_SHU_KADO_GRAPH' as const;

export const DataChainStoreShuKadoGraphActionTypes = {
  FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
  FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_REQUEST,
  FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_SUCCESS,
  SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
  SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_HALL,
  SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_KADO_TYPE,
  HIDE_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
  HIDE_ALL_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
  RENEW_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
};

// Action Creators

/**
 * 時間帯別稼働数データを取得する
 * @param shuCode 表示先テーブルの種別コード（店舗全体実績時:'null'）
 * @param params 時間帯別稼働数の検索条件
 */
function fetchDataChainStoreShuKadoGraphAction(
  shuCode: string,
  params: DataKadoGraphParams
) {
  return {
    type: FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
    payload: { shuCode, params },
  };
}

/**
 * 時間帯別稼働数データ取得前に実行する
 * @param shuCode 表示先テーブルの種別コード
 */
function fetchDataChainStoreShuKadoGraphRequestAction(shuCode: string) {
  return {
    type: FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_REQUEST,
    payload: { shuCode },
  };
}

/**
 * 時間帯別稼働数データ取得完了時データを登録する
 * @param shuCode 表示先テーブルの種別コード
 * @param graph 取得したグラフデータ
 */
function fetchDataChainStoreShuKadoGraphSuccessAction(
  shuCode: string,
  graph: DataKadoGraph
) {
  return {
    type: FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_SUCCESS,
    payload: { shuCode, graph },
  };
}

/**
 * 選択した店舗の時間帯別稼働数を表示する
 * @param selectOption 表示する店舗コードorエリアコード
 * @param shuOption 種別データ（空の場合:店舗全体実績）
 */
function searchDataChainStoreShuKadoGraphAction(
  selectOption: Pick<SelectOption, 'type' | 'value'>,
  shuOption?: ShuOption
) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
    payload: { selectOption, shuOption },
  };
}

/**
 * 指定した店舗でグラフを絞り込む（店舗選択フォーム用）
 * @param selectOption 表示する店舗コードorエリアコード
 * @param shuOption 種別データ（空の場合:店舗全体実績）
 */
function searchDataChainStoreShuKadoGraphHallAction(
  selectOption: Pick<SelectOption, 'type' | 'value'>,
  shuOption?: ShuOption
) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_HALL,
    payload: { shuOption, selectOption },
  };
}

/**
 * 指定した稼働数・稼働率で時間帯別稼働数データを再取得
 * @param kadoFieldType 変更後の稼働数・稼働率
 * @param shuOption 種別データ（空の場合:店舗全体実績）
 */
function searchDataChainStoreShuKadoGraphKadoTypeAction(
  kadoFieldType: KadoFieldType,
  shuOption?: ShuOption
) {
  return {
    type: SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_KADO_TYPE,
    payload: { shuOption, kadoFieldType },
  };
}

/**
 * チェーン店レポート 種別実績 時間帯別稼働数のデータを破棄する
 * @param shuCode 表示先テーブルの種別コード（店舗全体実績時:'null'）
 */
function hideDataChainStoreShuKadoGraphAction(shuCode: string) {
  return {
    type: HIDE_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
    payload: { shuCode },
  };
}

/**
 * チェーン店レポート 種別実績 時間帯別稼働数の全てのデータを破棄する
 */
function hideAllDataChainStoreShuKadoGraphAction() {
  return {
    type: HIDE_ALL_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
  };
}

/**
 * 初期化
 */
function renewDataChainStoreShuKadoGraphAction() {
  return {
    type: RENEW_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
  };
}

export const DataChainStoreShuKadoGraphActionCreators = {
  fetchDataChainStoreShuKadoGraphAction,
  fetchDataChainStoreShuKadoGraphRequestAction,
  fetchDataChainStoreShuKadoGraphSuccessAction,
  searchDataChainStoreShuKadoGraphAction,
  searchDataChainStoreShuKadoGraphHallAction,
  searchDataChainStoreShuKadoGraphKadoTypeAction,
  hideDataChainStoreShuKadoGraphAction,
  hideAllDataChainStoreShuKadoGraphAction,
  renewDataChainStoreShuKadoGraphAction,
};

// Actions

export type FetchDataChainStoreShuKadoGraphAction = ReturnType<
  typeof fetchDataChainStoreShuKadoGraphAction
>;
export type SearchDataChainStoreShuKadoGraphAction = ReturnType<
  typeof searchDataChainStoreShuKadoGraphAction
>;
export type SearchDataChainStoreShuKadoGraphHallAction = ReturnType<
  typeof searchDataChainStoreShuKadoGraphHallAction
>;
export type SearchDataChainStoreShuKadoGraphKadoTypeAction = ReturnType<
  typeof searchDataChainStoreShuKadoGraphKadoTypeAction
>;
type HideDataChainStoreShuKadoGraphAction = ReturnType<
  typeof hideDataChainStoreShuKadoGraphAction
>;
type HideAllDataChainStoreShuKadoGraphAction = ReturnType<
  typeof hideAllDataChainStoreShuKadoGraphAction
>;

type DataChainStoreShuKadoGraphAction =
  | FetchDataChainStoreShuKadoGraphAction
  | SearchDataChainStoreShuKadoGraphAction
  | SearchDataChainStoreShuKadoGraphHallAction
  | SearchDataChainStoreShuKadoGraphKadoTypeAction
  | HideDataChainStoreShuKadoGraphAction
  | HideAllDataChainStoreShuKadoGraphAction
  | ReturnType<typeof fetchDataChainStoreShuKadoGraphRequestAction>
  | ReturnType<typeof fetchDataChainStoreShuKadoGraphSuccessAction>
  | ReturnType<typeof renewDataChainStoreShuKadoGraphAction>;

// State

type DataChainStoreShuKadoGraphState = {
  /**
   * グラフデータ
   */
  dataChainStoreShuKadoGraph: {
    [key: string]: DataKadoGraph | undefined;
  };
  /**
   * ローディング状態
   */
  isLoading: {
    [key: string]: boolean | undefined;
  };
};

const initialState: DataChainStoreShuKadoGraphState = {
  dataChainStoreShuKadoGraph: {},
  isLoading: {},
};

// Selector

/**
 * 全てのグラフデータを取得する
 * @returns 全てのグラフデータ
 */
const dataChainStoreShuKadoGraphAllSelector = (state: RootState) =>
  state.dataChainStoreShuKadoGraph.dataChainStoreShuKadoGraph;

/**
 * 種別を元にグラフデータを取得する
 * @param shuCode 種別コード
 * @returns グラフデータ
 */
export const dataChainStoreShuKadoGraphSelector = (shuCode: string) =>
  createSelector(
    dataChainStoreShuKadoGraphAllSelector,
    (data) => data[shuCode]
  );

/**
 * 種別を元に時間帯別稼働グラフのデータが存在するかを取得する。
 * データ本体（重いオブジェクト）ではなくboolean だけを購読したい存在判定・ローディング判定向け。
 * @param shuCode 種別コード（店舗全体実績時:'null'）
 */
export const dataChainStoreShuKadoGraphIsExistSelector = (shuCode: string) =>
  createSelector(
    dataChainStoreShuKadoGraphAllSelector,
    (data) => data[shuCode] !== undefined
  );

/**
 * 時間帯別稼働数の検索条件を取得する
 * @param shuCode 種別コード
 * @returns 検索条件
 */
export const dataChainStoreShuKadoGraphSearchConditionSelector = (
  shuCode: string
) =>
  createSelector(
    dataChainStoreShuKadoGraphSelector(shuCode),
    (data) => data?.setting ?? {}
  );

/**
 * 全てのローディング状態を取得する
 * @returns 全てのローディング状態
 */
const dataChainStoreShuKadoGraphLoadingAllSelector = (state: RootState) =>
  state.dataChainStoreShuKadoGraph.isLoading;

/**
 * 種別を元にグラフのローディング状態を取得する
 * @param shuCode 種別コード
 * @returns ローディング状態
 */
export const dataChainStoreShuKadoGraphLoadingSelector = (shuCode: string) =>
  createSelector(
    dataChainStoreShuKadoGraphLoadingAllSelector,
    (loading) => loading[shuCode] ?? false
  );

/**
 * 選択された店舗を取得する
 * @param shuCode 種別コード
 * @returns 店舗一覧
 */
const dataChainStoreShuKadoGraphHallSelector = (shuCode: string) =>
  createSelector(
    dataChainStoreShuKadoGraphSearchConditionSelector(shuCode),
    (params) => {
      return getSelectedHallsAndAreas(params);
    }
  );

/**
 * 店舗一覧（SelectOption）を取得する
 *
 * エリア集計スイッチの有効時にエリアも表示される
 * 店舗は重複可能性があるため、重複の除外処理を行う
 *
 * @param shuCode 種別コード
 * @returns 店舗一覧（Selectで使える形式）
 */
export const dataChainStoreShuKadoGraphHallOptionSelector = (shuCode: string) =>
  createSelector(
    [
      dataChainStoreShuDataByShuSelector(shuCode),
      dataChainStoreShuWholeDataSelector,
    ],
    (dataChainStoreShu, dataChainStoreShuWhole) => {
      // 店舗コードがnullの場合は店舗全体実績のテーブルデータを使う
      const tableData =
        shuCode === 'null' ? dataChainStoreShuWhole : dataChainStoreShu;

      // チェックボックスでチェック入れた店舗情報をテーブルのデータから取得
      const result: SelectOption[] = tableData.data.rows
        .map((row) => ({
          name: row.data.at(0)?.value ?? '',
          value: row.queryParameter.value ?? 'null',
          type: row.queryParameter.name,
        }))
        .filter(
          (option, index, self) =>
            index ===
            self.findIndex(
              (selectOption) =>
                selectOption.value === option.value &&
                selectOption.type === option.type
            )
        );

      return result;
    }
  );

/**
 * 絞り込んでいる店舗の一覧（SelectOption)を取得する
 * @param shuCode 種別コード
 * @returns 絞り込んでいる店舗一覧(Selectで使える形式)
 */
export const dataChainStoreShuKadoGraphSelectedHallListOptionSelector = (
  shuCode: string
) =>
  createSelector(
    [
      dataChainStoreShuKadoGraphHallSelector(shuCode),
      dataChainStoreShuKadoGraphHallOptionSelector(shuCode),
    ],
    (selectedHallAreaList, hallsAreasList) => {
      const result =
        selectedHallAreaList.value === 'null'
          ? {
              name: '店舗平均',
              value: 'null',
              type: 'halls',
            }
          : hallsAreasList.find((v) =>
              selectedHallAreaList.value.includes(v.value)
            );

      if (result == null) {
        // エリアと店舗の不整合が発生したときには店舗平均を表示させる
        // エラーをスローした場合、ErrorBoundaryが表示されてお気に入りの状態が維持されなくなるため
        // sentry: https://b2defa61f433.sentry.io/issues/4986274332/?environment=prd&project=4506188805242880&query=is%3Aunresolved&referrer=issue-stream&sort=user&statsPeriod=7d&stream_index=8
        return {
          name: '店舗平均',
          value: 'null',
          type: 'halls',
        };
      }

      return result;
    }
  );

/**
 * 選択中の稼働数・稼働率を取得する
 * @param shuCode 種別コード
 * @returns 稼働数・稼働率
 */
export const dataChainStoreShuKadoGraphSelectedKadoTypeSelector = (
  shuCode: string
) =>
  createSelector(
    dataChainStoreShuKadoGraphSearchConditionSelector(shuCode),
    (params) => {
      return params.kadoFieldType ?? KADO_FIELD_TYPE.VALUE;
    }
  );

/**
 * 種別実績 時間帯別稼働数グラフのすべての検索条件を取得する（お気に入り保存用）
 * @returns 現在の種別実績 時間帯別稼働数グラフのすべての検索条件
 */
export const dataChainStoreShuKadoGraphSettingSelector = createSelector(
  dataChainStoreShuKadoGraphAllSelector,
  (data) => {
    return Object.fromEntries(
      Object.entries(data).map((entry) => {
        const [key, value] = entry;
        return [key, value?.setting];
      })
    );
  }
);

export function dataChainStoreShuKadoGraphReducer(
  state = initialState,
  action: DataChainStoreShuKadoGraphAction
): DataChainStoreShuKadoGraphState {
  switch (action.type) {
    case FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_REQUEST:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.shuCode]: true,
        },
      };
    case FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_SUCCESS:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.shuCode]: false,
        },
        dataChainStoreShuKadoGraph: {
          ...state.dataChainStoreShuKadoGraph,
          [action.payload.shuCode]: action.payload.graph,
        },
      };
    case HIDE_DATA_CHAIN_STORE_SHU_KADO_GRAPH:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.shuCode]: undefined,
        },
        dataChainStoreShuKadoGraph: {
          ...state.dataChainStoreShuKadoGraph,
          [action.payload.shuCode]: undefined,
        },
      };
    case HIDE_ALL_DATA_CHAIN_STORE_SHU_KADO_GRAPH:
      return {
        ...state,
        isLoading: initialState.isLoading,
        dataChainStoreShuKadoGraph: initialState.dataChainStoreShuKadoGraph,
      };
    case RENEW_DATA_CHAIN_STORE_SHU_KADO_GRAPH:
      return initialState;
    default:
      return state;
  }
}
